import { MessagingChannelReplyMode } from '@treadinc/horizon-api-spec';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useRef } from 'react';

import {
  MessagingChannel,
  MessagingMessage,
  principalId,
} from '~hooks/useMessaging/models';
import useMessagingChannelTypers, {
  TYPING_PING_INTERVAL_IN_MS,
} from '~hooks/useMessaging/useMessagingChannelTypers';
import { Nullable } from '~types/Nullable';

export default function useMessagingTyping({
  channel,
  isLoadingMessages,
  messages,
  viewerUserId,
}: {
  channel: Nullable<MessagingChannel>;
  isLoadingMessages: boolean;
  messages: MessagingMessage[];
  viewerUserId: Nullable<string> | undefined;
}) {
  const channelId = channel?.id;
  const { dropTyper, sendTyping, typers } = useMessagingChannelTypers({
    channelId,
    viewerUserId,
  });
  const lastPingAtRef = useRef(0);
  const seenMessageIdsRef = useRef<Nullable<Set<string>>>(null);
  const newestSeenAtRef = useRef<Nullable<Dayjs>>(null);

  useEffect(() => {
    if (isLoadingMessages) return;

    const seenMessageIds = seenMessageIdsRef.current;
    if (!seenMessageIds) {
      seenMessageIdsRef.current = new Set(messages.map((message) => message.id));
      newestSeenAtRef.current = messages.reduce<Nullable<Dayjs>>(
        (newest, message) =>
          !newest || message.createdAt.isAfter(newest) ? message.createdAt : newest,
        null,
      );
      return;
    }

    const newestSeenAt = newestSeenAtRef.current;
    let newestArrivalAt = newestSeenAt;

    messages.forEach((message) => {
      if (seenMessageIds.has(message.id)) return;
      seenMessageIds.add(message.id);

      const isOlderPage =
        newestSeenAt !== null && !message.createdAt.isAfter(newestSeenAt);
      if (isOlderPage) return;

      if (!newestArrivalAt || message.createdAt.isAfter(newestArrivalAt)) {
        newestArrivalAt = message.createdAt;
      }
      const senderId = principalId(message.sender);
      if (senderId) dropTyper(senderId);
    });

    newestSeenAtRef.current = newestArrivalAt;
  }, [dropTyper, isLoadingMessages, messages]);

  useEffect(
    () => () => {
      seenMessageIdsRef.current = null;
      newestSeenAtRef.current = null;
      lastPingAtRef.current = 0;
    },
    [channelId],
  );

  const repliesGoPrivately =
    channel?.replyMode === MessagingChannelReplyMode.REPLY_TO_SENDER &&
    principalId(channel.organizer) !== viewerUserId;

  const noteTyping = useCallback(() => {
    if (repliesGoPrivately) return;

    const now = Date.now();
    if (now - lastPingAtRef.current < TYPING_PING_INTERVAL_IN_MS) return;

    const wasSent = sendTyping();
    if (wasSent) lastPingAtRef.current = now;
  }, [repliesGoPrivately, sendTyping]);

  return { noteTyping, typers };
}
